// @flow

import * as React from 'react';
import {
  graphql,
  useLazyLoadQuery,
  useFragment,
  useMutation,
} from 'react-relay';
import { getRequest, createOperationDescriptor } from 'relay-runtime';
import { fetchQuery, useRelayEnvironment } from 'react-relay';
import { Flex, Box, useSystem } from 'react-system';
import { Divider, Button, Checkbox, AppBar, Dialog } from '@material-ui/core';
import { Carousel } from '@realadvisor/carousel';
import { Image } from '@realadvisor/image';
import { KeyboardArrowLeft } from '../../icons/keyboard-arrow-left';
import { Apartment } from '../../icons/apartment';
import { House } from '../../icons/house';
import { BureauCommerceIndustrie } from '../../icons/bureau-commerce-industrie';
import { ParkingSign } from '../../icons/parking-sign';
import { Area } from '../../icons/area';
import { Secondary } from '../../icons/secondary';
import { Gastro } from '../../icons/gastro';
import { Garden } from '../../icons/garden';
import { Agriculture } from '../../icons/agriculture';
import { VisibilityOff } from '../../icons/visibility-off';
import { Visibility } from '../../icons/visibility';
import { Favourite } from '../../icons/favourite';
import { Close } from '../../icons/close';
import { ArrowRightLong } from '../../icons/arrow-right-long';
import { OpenInNew } from '../../icons/open-in-new';
import { useTheme } from '../../theme';
import { useRouter } from '../../controls/router';
import { Link, type Href } from '../../controls/link';
import { useLocale } from '../../hooks/locale';
import { useUserData } from '../../hooks/user-data';
import { useNoSSR } from '../../hooks/nossr';
import { Gallery } from '../../controls/gallery';
import { Marked } from '../../controls/marked';
import { NoPhoto } from '../../controls/photo-helpers';
import { useAnalytics } from '../../controls/analytics';
import { NextImagePreload } from '../../controls/next-image-preload';
import { RatingComponent } from '../components/RatingComponent';
import { getAgencyLink } from '../../utils/agency-link';
import { getIdFromGlobalId } from '../../utils/global-id';
import { getListingDetailUrl } from '../../utils/listing-url.js';
import { AggregatesListingCampaignEnquiry } from './aggregates-listing-campaign-enquiry';
import { AggregatesListingBreadcrumbs } from './AggregatesListingBreadcrumbs';
import { AggregatesListingPropertyDetails } from './AggregatesListingPropertyDetails';
import { AggregatesListingCharacteristics } from './AggregatesListingCharacteristics';
import { AggregatesListingAnalytics } from './AggregatesListingAnalytics';
import { AggregatesListingEnergyDiagnostics } from './AggregatesListingEnergyDiagnostics';
import { useAggregatesListingH1 } from './AggregatesListingH1';
import { AggregatesListingCard } from './AggregatesListingCard';
import { AggregatesListingSaveFavorites } from './AggregatesListingSaveFavorites';
import { AggregatesListingStructuredData } from './AggregatesListingStructuredData';
import {
  AggregatesListingPriceCard,
  domainToSitename,
} from './AggregatesListingPriceCard';
import { AggregatesListingMap } from './aggregates-listing-map';
import { AggregatesListingLinkoutLogin } from './AggregatesListingLinkoutLogin';
import { AggregatesListingAlertDialog } from './aggregates-listing-alert-dialog';
import { useAlertDialogState } from './state';
import { paramsToFilters, type ListingsParams } from './listingsDescriptor';
import type { AggregatesListing_listing$key } from './__generated__/AggregatesListing_listing.graphql';
import type { AggregatesListing_aggregatesListings$key } from './__generated__/AggregatesListing_aggregatesListings.graphql';
import type { AggregatesListingSettingsQuery } from './__generated__/AggregatesListingSettingsQuery.graphql';
import type { AggregatesListingHideMutation } from './__generated__/AggregatesListingHideMutation.graphql';
import type { AggregatesListingClickoutInsertMutation } from './__generated__/AggregatesListingClickoutInsertMutation.graphql';
import type { AggregatesListingFavouriteInsertOrDeleteMutation } from './__generated__/AggregatesListingFavouriteInsertOrDeleteMutation.graphql';
import type { AggregatesListingCreateSearchMutation } from './__generated__/AggregatesListingCreateSearchMutation.graphql';
import type { AggregatesListingMeQuery } from './__generated__/AggregatesListingMeQuery.graphql';

type Props = {|
  listing: AggregatesListing_listing$key,
  shallowCardsLink: boolean,
  replaceCardsLink: boolean,
  backHref: Href,
  params?: ListingsParams,
  aggregatesListings?: ?AggregatesListing_aggregatesListings$key,
  // Used for client only
  onClose?: (e: SyntheticMouseEvent<>) => void,
|};

type CssProp = { [string]: mixed, ... };

type CenterProps = {|
  children?: React.Node,
  css?: CssProp,
  className?: string,
|};

// We display AggregatesListing in popup so using custom Center
const Center = (props: CenterProps) => {
  const theme = useTheme();
  const { media } = useSystem();

  return (
    <Box
      className={props.className ?? ''}
      px={[3, 5, 5]}
      width={1}
      mx="auto"
      css={[
        media({
          maxWidth: theme.maxWidths,
        }),
        props.css ?? {},
      ]}
    >
      {props.children}
    </Box>
  );
};

const BackToSearch = ({ backHref, onClose }) => {
  const { t /* , language */ } = useLocale();
  const { text } = useTheme();

  return (
    <Link href={backHref}>
      <Flex
        as="a"
        css={{ color: 'unset', textDecoration: 'unset' }}
        alignItems="center"
        onClick={e => {
          if (onClose) {
            onClose(e);
          }
        }}
      >
        <Box ml="-6px">
          <KeyboardArrowLeft size={20} />
        </Box>
        <Box css={text.subtitle2} ml={1}>
          {t('seeAllListings')}
        </Box>
      </Flex>
    </Link>
  );
};

const UserActions = ({ listingSlug }) => {
  const { t } = useLocale();
  const { media } = useSystem();
  const { colors, text } = useTheme();
  const { countryCode } = useUserData();

  const [dialogOpen, setDialogOpen] = React.useState(false);

  const data = useLazyLoadQuery<AggregatesListingSettingsQuery>(
    graphql`
      query AggregatesListingSettingsQuery($listingSlug: String!) {
        me {
          isAdmin
        }
        listing: aggregatesListingBySlug(slug: $listingSlug) {
          id
          isFavourite
          removed
        }
      }
    `,
    { listingSlug },
  );

  const [hideListing] = useMutation<AggregatesListingHideMutation>(
    graphql`
      mutation AggregatesListingHideMutation($input: HideListingInput!) {
        hideListing(input: $input) {
          aggregatesListing {
            id
            removed
          }
        }
      }
    `,
  );

  const [insertOrDeleteListingCardFavourite] =
    useMutation<AggregatesListingFavouriteInsertOrDeleteMutation>(
      graphql`
        mutation AggregatesListingFavouriteInsertOrDeleteMutation(
          $input: InsertOrDeleteListingFavouriteInput!
        ) {
          insertOrDeleteListingFavourite(input: $input) {
            aggregatesListing {
              id
              isFavourite
            }
          }
        }
      `,
    );

  if (data.listing == null) {
    return null;
  }

  const { me, listing } = data;

  const hiddenIcon = (
    <Checkbox
      checked={listing.removed}
      icon={<VisibilityOff size={30} fill="rgba(0, 0, 0, 0.5)" />}
      checkedIcon={<Visibility size={30} fill={colors.white} />}
    />
  );

  const favouriteIcon = (
    <Checkbox
      checked={listing.isFavourite}
      icon={<Favourite size={30} fill="rgba(0, 0, 0, 0.5)" />}
      checkedIcon={<Favourite size={30} fill={colors.red600} />}
    />
  );

  return (
    <>
      <AggregatesListingSaveFavorites
        open={dialogOpen}
        listingId={listing.id}
        onClose={() => setDialogOpen(false)}
      />
      <Center
        css={{
          display: 'flex',
          justifyContent: 'flex-start',
        }}
      >
        <Box>
          {data.me?.isAdmin === true && (
            <>
              <Button
                css={media({
                  display: ['none', 'flex'],
                  pointerEvents: 'all',
                  padding: '2px 8px',
                  marginTop: '8px',
                  marginRight: '8px',
                  border: 'none',
                  color: listing.removed ? colors.white : 'none',
                  backgroundColor: listing.removed
                    ? colors.red600
                    : colors.white,
                  '&:hover': {
                    backgroundColor: colors.red400,
                  },
                })}
                fullWidth={false}
                onClick={() => {
                  hideListing({
                    variables: {
                      input: {
                        id: listing.id,
                        removed: !listing.removed,
                        countryCode,
                      },
                    },
                  });
                }}
                variant="outlined"
                startIcon={hiddenIcon}
              >
                <Box pr={3} css={[text.subtitle1]}>
                  {listing.removed === true ? t('unhide') : t('hide')}
                </Box>
              </Button>

              <Box
                m={2}
                css={media({
                  display: ['flex', 'none'],
                  pointerEvents: 'all',
                })}
                onClick={() => {
                  hideListing({
                    variables: {
                      input: {
                        id: listing.id,
                        removed: !listing.removed,
                        countryCode,
                      },
                    },
                  });
                }}
              >
                {hiddenIcon}
              </Box>
            </>
          )}
        </Box>
        <Box>
          <Button
            css={media({
              display: ['none', 'flex'],
              pointerEvents: 'all',
              padding: '2px 8px',
              marginTop: '8px',
              border: 'none',
              backgroundColor: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
              },
            })}
            fullWidth={false}
            onClick={() => {
              if (me != null) {
                insertOrDeleteListingCardFavourite({
                  variables: {
                    input: {
                      listingId: listing.id,
                    },
                  },
                });
              } else {
                setDialogOpen(true);
              }
            }}
            variant="outlined"
            startIcon={favouriteIcon}
          >
            <Box pr={3} css={[text.subtitle1]}>
              {listing.isFavourite === true ? t('saved') : t('save')}
            </Box>
          </Button>

          <Box
            m={2}
            css={media({
              display: ['flex', 'none'],
              pointerEvents: 'all',
            })}
            onClick={() => {
              if (me != null) {
                insertOrDeleteListingCardFavourite({
                  variables: {
                    input: {
                      listingId: listing.id,
                    },
                  },
                });
              } else {
                setDialogOpen(true);
              }
            }}
          >
            {favouriteIcon}
          </Box>
        </Box>
      </Center>
    </>
  );
};

export const AggregatesListing: React.AbstractComponent<Props, mixed> =
  React.memo(props => {
    const noSsr = useNoSSR();
    const environment = useRelayEnvironment();

    const aggregatesListings = useFragment(
      graphql`
        fragment AggregatesListing_aggregatesListings on AggregatesListingConnectionConnection {
          ...aggregatesListingAlertDialog_aggregatesListings
        }
      `,
      props.aggregatesListings ?? null,
    );

    const listing = useFragment(
      graphql`
        fragment AggregatesListing_listing on AggregatesListing
        @argumentDefinitions(
          filters: { type: "AggregatesListingFiltersInput", defaultValue: null }
        ) {
          ...AggregatesListingAnalytics_listing
          ...AggregatesListingStructuredData_listing
          ...aggregatesListingMap_listing
          ...aggregatesListingCampaignEnquiry_listing
          id
          encodedId
          isListingCampaign
          isIdxListing
          offlineSince
          slug
          listingLanguage
          title
          translatedTitle
          livingSurface
          landSurface
          computedSurface
          offerType
          salePrice
          salePricePerLivingSurface
          salePricePerLandSurface
          grossRentMonthly
          grossRentM2yearly
          currency
          postcode
          route
          address
          streetNumber
          agencyId
          agencyName
          agencySlug
          agencyLogoUrl
          agencyRating
          agencyReviewsCount
          agencyContactPhoneNumber
          visitContactPerson
          visitContactPhoneNumber
          agencyReference
          lotReference
          # read just 100 image, as image names are random and gziped hardly
          images(first: 100) {
            bucketName
            fileName
            blurhash
          }
          similarListings(count: 4) {
            id
            encodedId
            slug
            title
            offerType
            currency
            salePrice
            grossRentMonthly
            constructionYear
            renovationYear
            livingSurface
            landSurface
            computedSurface
            numberOfRooms
            numberOfBathrooms
            numberOfParking
            propertyMainType
            propertyType
            compositePropertyType
            portal
            constructionYear
            renovationYear
            agencyId
            agencyName
            route
            streetNumber
            postcode
            subLocality
            locality
            lat
            lng
            url(filters: $filters) {
              url
            }
            ...AggregatesListingCard_listing
          }
          numberOfRooms
          numberOfBathrooms
          numberOfParking
          propertyType
          propertyMainType
          compositePropertyType
          portal
          constructionYear
          renovationYear
          ...AggregatesListingBreadcrumbs_listing
          ...AggregatesListingPropertyDetails_listing
          ...AggregatesListingCharacteristics_listing
          ...AggregatesListingPriceCard_listing
          ...AggregatesListingPriceCardContent_listing
          ...AggregatesListingH1_listing
          ...AggregatesListingEnergyDiagnostics_listing
          url {
            url
            originalDomain
          }
          bookOnline
          urls {
            url
            originalDomain
            mergedListingId
          }
          locality
          subLocality
          localityObject {
            label
            slug
            canonicalPlace {
              label
              slug
            }
          }
          municipalityObject {
            label
            slug
            objectId
          }
          stateObject {
            label
            extra {
              canton_code
            }
          }
          lat
          lng
          description
          translatedDescription
          agencyReferences
          similarListingsStats {
            saleOffersCount
            rentOffersCount
          }
        }
      `,
      props.listing,
    );

    const [me, setMe] = React.useState<null | {|
      isAdmin: boolean,
      isBroker: boolean,
    |}>(null);
    React.useEffect(() => {
      const query = graphql`
        query AggregatesListingMeQuery {
          me {
            isAdmin
            isBroker
          }
        }
      `;
      const variables = {};
      fetchQuery<AggregatesListingMeQuery>(environment, query, variables, {
        // prevent fetching if already loaded
        fetchPolicy: 'store-or-network',
      }).subscribe({
        next: data => {
          // prevent gc me from store
          const request = getRequest(query);
          const operation = createOperationDescriptor(request, variables);
          environment.retain(operation);
          if (data.me != null) {
            setMe({
              isBroker: data.me.isBroker === true,
              isAdmin: data.me.isAdmin === true,
            });
          }
        },
      });
    }, [environment]);

    const { colors, text, shape } = useTheme();
    const { media } = useSystem();
    const {
      t,
      language,
      numberLocale,
      currency: fallbackCurrency,
    } = useLocale();
    const { countryDomain } = useUserData();
    const { track } = useAnalytics();
    const router = useRouter();
    const iconStyle = media({ width: [22, 25, 32], height: [22, 25, 32] });

    const [insertClickout] =
      useMutation<AggregatesListingClickoutInsertMutation>(
        graphql`
          mutation AggregatesListingClickoutInsertMutation(
            $input: InsertClickoutInput!
          ) {
            insertClickout(input: $input) {
              clientMutationId
            }
          }
        `,
      );

    const [upsertPropertySearch] =
      useMutation<AggregatesListingCreateSearchMutation>(
        graphql`
          mutation AggregatesListingCreateSearchMutation(
            $input: UpsertPropertySearchInput!
          ) {
            upsertPropertySearch(input: $input) {
              propertySearch {
                id
              }
            }
          }
        `,
      );

    const h1Title = useAggregatesListingH1(listing);

    const propertyMainTypes = {
      APPT: t('appartement'),
      HOUSE: t('house'),
      INDUS: t('bureauCommerceIndustrie'),
      PARK: t('parking'),
      PROP: t('plot'),
      SECONDARY: t('otherProperty'),
      GASTRO: t('hotelCafeRestaurant'),
      GARDEN: t('garden'),
      AGRI: t('agriculturalPropertyCapital'),
    };

    const propertyTypeIcon = type => {
      switch (type) {
        case 'APPT':
          return <Apartment css={iconStyle} />;
        case 'HOUSE':
          return <House css={iconStyle} />;
        case 'INDUS':
          return <BureauCommerceIndustrie css={iconStyle} />;
        case 'PARK':
          return <ParkingSign css={iconStyle} />;
        case 'PROP':
          return <Area css={iconStyle} />;
        case 'SECONDARY':
          return <Secondary css={iconStyle} />;
        case 'GASTRO':
          return <Gastro css={iconStyle} />;
        case 'GARDEN':
          return <Garden css={iconStyle} />;
        case 'AGRI':
          return <Agriculture css={iconStyle} />;
        default:
          return null;
      }
    };

    const [galleryVisibleAt, setGalleryVisibleAt] = React.useState(null);
    const renderItemHeadMainProperty = (value, label, index) => (
      <Box
        key={index}
        flexGrow={[1, 0]}
        css={{ whiteSpace: 'nowrap' }}
        pr={[0, 5]}
        mb={3}
        pl={2}
      >
        <Box css={media([text.subtitle2, text.h6, text.h5])}>{value}</Box>
        <Box css={media([text.caption, text.body1])} mt={[0, 1]}>
          {label}
        </Box>
      </Box>
    );

    const {
      isListingCampaign,
      offlineSince,
      listingLanguage,
      title,
      translatedTitle,
      numberOfRooms,
      numberOfBathrooms,
      livingSurface,
      landSurface,
      offerType,
      salePrice,
      salePricePerLivingSurface,
      salePricePerLandSurface,
      grossRentMonthly,
      grossRentM2yearly,
      currency,
      postcode,
      route,
      address,
      stateObject,
      streetNumber,
      images,
      similarListings,
      propertyMainType,
      url,
      bookOnline,
      urls,
      subLocality,
      description,
      translatedDescription,
      agencyReferences,
      agencyId,
      agencyName,
      agencySlug,
      agencyLogoUrl,
      agencyRating,
      agencyReviewsCount,
      agencyContactPhoneNumber,
      visitContactPerson,
      visitContactPhoneNumber,
    } = listing;

    const cantonCode = stateObject?.extra?.canton_code;

    const salePricePerComputedSurface =
      salePricePerLivingSurface != null
        ? salePricePerLivingSurface
        : salePricePerLandSurface;

    const showTranslationToggle =
      listingLanguage !== language && translatedDescription != null;

    const [showTranslatedDescription, setShowTranslatedDescription] =
      React.useState(true);

    const getLanguageName = listingLanguage => {
      switch (listingLanguage) {
        case 'en':
          return t('descriptionLanguage_en');
        case 'fr':
          return t('descriptionLanguage_fr');
        case 'de':
          return t('descriptionLanguage_de');
        case 'it':
          return t('descriptionLanguage_it');
        default:
          return null;
      }
    };

    const [sendEnquiryDialog, setSendEnquiryDialog] = React.useState(false);
    const alertDialogState = useAlertDialogState();

    const [showAgencyPhoneNumber, setShowAgencyPhoneNumber] =
      React.useState(false);
    const [showVisitPhoneNumber, setShowVisitPhoneNumber] =
      React.useState(false);

    const handleShowPhoneNumber = setStateAction => {
      // Adding delay so link href attribute is set after click event
      // and link won't be opened when toggling phone number.
      setTimeout(() => {
        setStateAction(true);
      }, 250);

      window.dataLayer.push({
        event: 'show_phone_number',
        app: 'aggregator',
        aggregator: listing.url?.originalDomain,
        id: listing.id,
        slug: listing.slug,
        offer_type: listing.offerType,
        page: window.location.href,
        page_type: 'listing-detail',
        agency_id: agencyId,
        agency_name: listing.agencyName,
        agency_reference: listing.agencyReference,
        agency_contact_phone_number: listing.agencyContactPhoneNumber,
        visit_contact_person: listing.visitContactPerson,
        visit_contact_phone_number: listing.visitContactPhoneNumber,
      });
    };

    const formatCurrency = val =>
      numberLocale.format(val, {
        style: 'currency',
        currency: currency ?? fallbackCurrency,
        currencyDisplay: 'symbol',
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      });

    const realPrice = offerType === 'buy' ? salePrice : grossRentMonthly;

    const formatNumber = val =>
      numberLocale.format(val, {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      });

    const headMainPropsFirst = [
      {
        value:
          propertyMainType != null ? propertyTypeIcon(propertyMainType) : null,
        label:
          propertyMainType != null ? propertyMainTypes[propertyMainType] : '',
      },
      {
        value:
          livingSurface != null ? `${formatNumber(livingSurface)} m²` : null,
        label: livingSurface != null ? t('livingSurface') : '',
      },
      {
        value: propertyMainType === 'PROP' ? null : numberOfRooms ?? null,
        label: t('rooms'),
      },
      {
        value:
          landSurface != null && propertyMainType === 'HOUSE'
            ? `${formatNumber(landSurface)} m²`
            : null,
        label: t('plot'),
      },
      {
        value: propertyMainType === 'PROP' ? null : numberOfBathrooms ?? null,
        label: t('bathrooms'),
      },
    ].slice(0, 4);

    const onLinkClick = listing => (evt: SyntheticEvent<HTMLLinkElement>) => {
      const dialogOpened = alertDialogState.conditionallyOpen(props.params);
      if (dialogOpened) {
        evt.preventDefault();
      }
      track('RA.Web.Listing.SuggestionClick', {
        category: 'Click',
        id: listing.id,
        encodedId: listing.encodedId,
        offerType: listing.offerType,
        currency: listing.currency,
        salePrice: listing.salePrice,
        grossRentMonthly: listing.grossRentMonthly,
        constructionYear: listing.constructionYear,
        renovationYear: listing.renovationYear,
        livingSurface: listing.livingSurface,
        landSurface: listing.landSurface,
        numberOfRooms: listing.numberOfRooms,
        numberOfBathrooms: listing.numberOfBathrooms,
        propertyMainType: listing.propertyMainType,
        route: listing.route,
        streetNumber: listing.streetNumber,
        postcode: listing.postcode,
        locality: listing.locality,
        lat: listing.lat,
        lng: listing.lng,
        url: listing.url?.url,
      });
    };

    const [openLinkoutLogin, setOpenLinkoutLogin] = React.useState(false);
    const [linkoutUrl, setLinkoutUrl] = React.useState(null);

    const onLinkoutClick = ({
      originalDomain,
      url: clickoutId,
      mergedListingId,
    }) => {
      track('RA.Web.Listing.Linkout.Clicked', {
        category: 'Clickout',
        label: originalDomain,
        id: mergedListingId,
        encodedId: listing.encodedId,
        slug: listing.slug,
        offerType: listing.offerType,
        propertyType: listing.propertyType,
        propertyMainType: listing.propertyMainType,
        compositePropertyType: listing.compositePropertyType,
        numberOfRooms: listing.numberOfRooms,
        numberOfBathrooms: listing.numberOfBathrooms,
        livingSurface: listing.livingSurface,
        landSurface: listing.landSurface,
        salePrice: listing.salePrice,
        grossRentMonthly: listing.grossRentMonthly,
        currency: listing.currency,
        constructionYear: listing.constructionYear,
        locality: listing.locality,
        state: listing.stateObject?.label,
        municipality: listing.municipalityObject?.label,
        streetNumber: listing.streetNumber,
        route: listing.route,
        address: listing.address,
        postcode: listing.postcode,
        lat: listing.lat,
        lng: listing.lng,
        agencyName: listing.agencyName,
        agencyReference: listing.agencyReference,
        lotReference: listing.lotReference,
        url: listing.url?.url,
      });

      window.dataLayer.push({
        event: 'click_out_aggregator',
        aggregator: originalDomain,
        id: mergedListingId,
        slug: listing.slug,
        offer_type: listing.offerType,
        property_type: listing.propertyType,
        property_main_type: listing.propertyMainType,
        composite_property_type: listing.compositePropertyType,
        number_of_rooms: listing.numberOfRooms,
        number_of_bathrooms: listing.numberOfBathrooms,
        living_surface: listing.livingSurface,
        land_surface: listing.landSurface,
        sale_price: listing.salePrice,
        gross_rent_monthly: listing.grossRentMonthly,
        currency: listing.currency,
        construction_year: listing.constructionYear,
        locality: listing.locality,
        state: listing.stateObject?.label,
        municipality: listing.municipalityObject?.label,
        street_number: listing.streetNumber,
        route: listing.route,
        address: listing.address,
        postcode: listing.postcode,
        lat: listing.lat,
        lng: listing.lng,
        agency_name: listing.agencyName,
        agency_reference: listing.agencyReference,
        lot_reference: listing.lotReference,
        url: listing.url?.url,
      });

      const url = router.getUrl({
        pathname: 'listing-clickout',
        query: { id: clickoutId, listingSlug: listing.slug },
      }).absolute;

      if (me != null) {
        insertClickout({
          variables: {
            input: {
              listingId: mergedListingId,
            },
          },
        });

        if (props.params != null) {
          const filters = paramsToFilters(props.params);

          // use municipality as default property search place if there's no place in filters
          if (
            !filters.placeSlugs?.length &&
            !filters.placesById?.length &&
            listing.municipalityObject != null
          ) {
            filters.placesById = [
              {
                type: 'municipality',
                objectId: listing.municipalityObject.objectId,
              },
            ];
          }

          upsertPropertySearch({
            variables: {
              input: {
                propertySearch: {
                  initCreatedAtGte: true,
                  activateAggregator: true,
                  notification: 'daily',
                  source: 'aggregator',
                  aggregatorSource: 'listing_clickout',
                  filters,
                },
              },
            },
          });
        }

        window.open(url, '_blank');
      } else {
        setLinkoutUrl(url);
        setOpenLinkoutLogin(true);
      }
    };

    const canonicalPlace =
      listing.localityObject?.canonicalPlace ?? listing.localityObject;
    const similarDataCount =
      listing.offerType === 'buy'
        ? listing.similarListingsStats.saleOffersCount
        : listing.similarListingsStats.rentOffersCount;
    const similarDataLabel = canonicalPlace?.label ?? '';

    React.useEffect(() => {
      const linkoutUrl = router.query.linkoutUrl;
      if (typeof router.query.linkoutUrl === 'string') {
        setTimeout(() => {
          window.location = linkoutUrl;
        }, 300);
      }
    }, [router.query.linkoutUrl]);

    // Track listing views
    React.useEffect(() => {
      const price =
        listing.offerType === 'buy'
          ? listing.salePrice
          : listing.grossRentMonthly;

      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'view_item',
        ecommerce: {
          currency: 'CHF',
          value: price,
          items: [
            {
              item_id: getIdFromGlobalId(listing.id),
              item_name: listing.title,
              item_category: listing.offerType,
              item_category2: listing.propertyMainType,
              item_category3: listing.propertyType,
              price,
              url: [
                window.location.origin,
                getListingDetailUrl(
                  listing.offerType,
                  listing.compositePropertyType,
                  listing.slug,
                ),
              ].join(''),
              portal: listing.portal,
              postcode: listing.postcode,
              locality: listing.subLocality,
              municipality: listing.locality,
              lat: listing.lat,
              lng: listing.lng,
              computed_surface: listing.computedSurface,
              number_of_rooms: listing.numberOfRooms,
              number_of_bathrooms: listing.numberOfBathrooms,
              number_of_parkings: listing.numberOfParking,
              construction_year: listing.constructionYear,
              renovation_year: listing.renovationYear,
              agency_id: listing.agencyId,
              agency_name: listing.agencyName,
            },
          ],
        },
      });
    }, [listing]);

    // Track similar listing views
    React.useEffect(() => {
      if (similarListings.length > 0) {
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
          event: 'view_item_list',
          ecommerce: {
            currency: 'CHF',
            item_list_id: 'similar_listings',
            items: similarListings.map((listing, index) => ({
              item_id: getIdFromGlobalId(listing.id),
              item_name: listing.title,
              index,
              item_category: listing.offerType,
              item_category2: listing.propertyMainType,
              item_category3: listing.propertyType,
              price:
                listing.offerType === 'buy'
                  ? listing.salePrice
                  : listing.grossRentMonthly,
              url: [
                window.location.origin,
                getListingDetailUrl(
                  listing.offerType,
                  listing.compositePropertyType,
                  listing.slug,
                ),
              ].join(''),
              portal: listing.portal,
              postcode: listing.postcode,
              locality: listing.subLocality,
              municipality: listing.locality,
              lat: listing.lat,
              lng: listing.lng,
              computed_surface: listing.computedSurface,
              number_of_rooms: listing.numberOfRooms,
              number_of_bathrooms: listing.numberOfBathrooms,
              number_of_parkings: listing.numberOfParking,
              construction_year: listing.constructionYear,
              renovation_year: listing.renovationYear,
              agency_id: listing.agencyId,
              agency_name: listing.agencyName,
            })),
          },
        });
      }
    }, [similarListings]);

    // Track agency impressions for agencies linked to CRM teams
    React.useEffect(() => {
      if (agencyId != null && agencyName != null) {
        window.dataLayer.push({
          event: 'agency_impression',
          app: 'aggregator',
          page_type: 'listing-detail',
          page: window.location.href,
          agency_id: agencyId,
          agency_name: agencyName,
        });
      }
    }, [agencyId, agencyName]);

    const CAROUSEL_IMAGE_RATIO = 0.66;
    const CAROUSEL_IMAGE_SRCSET = [
      [375, 375 * CAROUSEL_IMAGE_RATIO],
      [600, 600 * CAROUSEL_IMAGE_RATIO],
    ];

    const CAROUSEL_MEDIA = [
      {
        itemsOnScreen: 1,
        aspectRatio: CAROUSEL_IMAGE_RATIO,
        gap: 1,
      },
      {
        itemsOnScreen: 2.3,
        aspectRatio: CAROUSEL_IMAGE_RATIO,
        gap: 6,
      },
      {
        itemsOnScreen: 2.3,
        aspectRatio: CAROUSEL_IMAGE_RATIO,
        gap: 12,
      },
    ];

    return (
      <>
        <AggregatesListingAnalytics listing={listing} />
        <AggregatesListingStructuredData listing={listing} />
        {props.params != null && (
          <AggregatesListingAlertDialog
            open={alertDialogState.isOpen}
            params={props.params}
            aggregatesListings={aggregatesListings}
            onClose={alertDialogState.close}
            source="listing_similar_click"
          />
        )}
        {isListingCampaign === true && (
          <Dialog
            open={sendEnquiryDialog}
            onClose={() => setSendEnquiryDialog(false)}
          >
            <Box
              // prevent closing map popup
              data-invisible-for-click-outside="true"
              p={3}
              css={{ height: '80%' }}
            >
              <Box px={3} pb={3} css={text.subtitle0}>
                {t('contactAdvertiser')}
              </Box>

              <button
                css={{
                  position: 'absolute',
                  right: 8,
                  top: 6,
                  appearance: 'none',
                  WebkitTapHighlightColor: 'transparent',
                  backgroundColor: 'transparent',
                  border: 'none',
                  padding: 12,
                  opacity: 0.6,
                  cursor: 'pointer',
                  borderRadius: '100%',
                  outline: 'none',
                  '&:focus': {
                    backgroundColor: 'rgba(0,0,0,0.2)',
                  },
                }}
                onClick={() => setSendEnquiryDialog(false)}
              >
                <Close size={24} />
              </button>

              <AggregatesListingCampaignEnquiry
                isMobile={true}
                onClose={() => setSendEnquiryDialog(false)}
                listing={listing}
              />
            </Box>
          </Dialog>
        )}
        <AggregatesListingLinkoutLogin
          listingId={listing.id}
          linkoutUrl={linkoutUrl}
          open={openLinkoutLogin}
          onClose={() => {
            setOpenLinkoutLogin(false);
          }}
          onCancel={() => {
            setOpenLinkoutLogin(false);
            if (linkoutUrl != null) {
              window.open(linkoutUrl, '_blank');
            }
          }}
        />

        <Box
          css={media({
            display: ['block', 'none'],
            minHeight: 'auto',
          })}
          mb={2}
          mt={[2, 0]}
        >
          <Center>
            <BackToSearch backHref={props.backHref} onClose={props.onClose} />
          </Center>
        </Box>

        <div css={{ position: 'relative' }}>
          <Carousel
            media={CAROUSEL_MEDIA}
            fillEmpty={true}
            // Render initially no more than 3 images
            // This highly reduces amount of picture elements on rendering
            renderUntilTouched={3}
          >
            {images.length === 0 || offlineSince != null ? (
              <NoPhoto />
            ) : (
              images.map((image, index) => (
                <div
                  key={index}
                  css={{
                    cursor: 'pointer',
                    width: '100%',
                    height: '100%',
                    WebkitTapHighlightColor: 'transparent',
                  }}
                  onClick={() => setGalleryVisibleAt(index)}
                >
                  <Image
                    src={{
                      fileName: image.fileName,
                      bucketName: image.bucketName,
                    }}
                    srcSize={CAROUSEL_IMAGE_SRCSET}
                    alt={`${translatedTitle ?? h1Title}. Photo ${index + 1}.`}
                    loading={index < 3 ? undefined : 'lazy'}
                    blurhash={image.blurhash}
                    PreloadComponent={index < 3 ? NextImagePreload : undefined}
                  />
                </div>
              ))
            )}
          </Carousel>
          {noSsr && (
            <div
              css={{
                position: 'absolute',
                left: 0,
                top: 0,
                right: 0,
                pointerEvents: 'none',
                // zIndex is needed to fix scroll mobile issue
                // when you can break scroll just touching carousel during scroll
                zIndex: 1,
              }}
            >
              <React.Suspense fallback={null}>
                <UserActions listingSlug={listing.slug} />
              </React.Suspense>
            </div>
          )}
        </div>

        {galleryVisibleAt != null && (
          <div
            css={media({
              '& > div': {
                marginTop: ['56px', '0px'],
              },
            })}
          >
            <Gallery
              startAt={galleryVisibleAt}
              images={images}
              onClose={() => setGalleryVisibleAt(null)}
              altPrefix={translatedTitle ?? h1Title}
            />
          </div>
        )}
        <Center>
          <Flex alignItems="flex-start">
            <Box flexGrow={1} flexShrink={1} flexBasis={0}>
              {/** Header  */}

              <Box pr={[0, 2]}>
                <Flex
                  mt={[2, 3]}
                  mb={2}
                  alignItems="left"
                  flexDirection="column"
                >
                  <Box
                    mb={[2, 3]}
                    css={media([
                      // media({ display: ['block', 'none'] }),
                      text.subtitle1,
                      text.h4,
                    ])}
                    as="h1"
                  >
                    {h1Title}
                  </Box>

                  <Box
                    width={1}
                    mb={[3, 3, 0]}
                    css={media({ display: ['unset', 'none'] })}
                  >
                    <Box css={{ whiteSpace: 'nowrap' }}>
                      {realPrice != null && realPrice !== 0 ? (
                        <span css={media([text.h5, text.h4])}>
                          {`${formatCurrency(realPrice)}${
                            countryDomain !== 'fr' ? '.-' : ''
                          }${offerType === 'rent' ? t('monthly') : ''}`}
                        </span>
                      ) : grossRentM2yearly != null &&
                        grossRentM2yearly >= 1.0 ? (
                        <Box css={text.h5}>
                          {`${formatCurrency(grossRentM2yearly)} ${t(
                            'totalRentM2Year',
                          )}`}
                        </Box>
                      ) : (
                        <span css={media([text.h5, text.h4])}>
                          {t('onRequest')}
                        </span>
                      )}
                    </Box>

                    {salePricePerComputedSurface != null && (
                      <Box
                        css={media([text.body2, text.body1])}
                        mt={['2px', 1]}
                      >
                        {formatCurrency(salePricePerComputedSurface)}
                        {' / '}m²
                      </Box>
                    )}

                    <Box pt={3}>
                      <Divider />
                    </Box>
                  </Box>

                  {/** Table head mobile/Desktop*/}
                  <Box flexGrow={1} css={{ minWidth: '240px' }} ml={-2}>
                    <Flex flexWrap="wrap">
                      {headMainPropsFirst.map(
                        (item, inx) =>
                          item.value != null &&
                          renderItemHeadMainProperty(
                            item.value,
                            item.label,
                            inx,
                          ),
                      )}
                    </Flex>
                  </Box>
                </Flex>
              </Box>

              {/* TODO: Marked is heavy, make lazy */}
              {description != null && (
                <Box pr={[0, 2]} py={3} mb={2}>
                  <Box pb={3} css={media([text.h6, text.h5])}>
                    {t('description')}
                    <Box css={[text.body2]}>
                      {showTranslationToggle && (
                        <>
                          {listingLanguage != null &&
                            (showTranslatedDescription
                              ? t('descriptionTranslatedFrom', {
                                  language: getLanguageName(listingLanguage),
                                })
                              : t('descriptionOriginalDescriptionLanguage', {
                                  language: getLanguageName(listingLanguage),
                                }))}{' '}
                          <button
                            onClick={() =>
                              setShowTranslatedDescription(
                                !showTranslatedDescription,
                              )
                            }
                            css={[text.body2, text.linkToggle]}
                          >
                            {showTranslatedDescription
                              ? t('descriptionShowOriginal')
                              : t('descriptionTranslateIn')}
                          </button>
                        </>
                      )}
                    </Box>
                  </Box>
                  <Box as="h2" mb={2} css={[text.subtitle0]}>
                    {translatedTitle != null && showTranslatedDescription
                      ? translatedTitle
                      : title}
                  </Box>
                  <Box
                    css={[
                      text.body1,
                      {
                        code: { whiteSpace: 'normal' },
                        overflowWrap: 'break-word',
                      },
                    ]}
                  >
                    <Marked links={false}>
                      {translatedDescription != null &&
                      showTranslatedDescription
                        ? translatedDescription
                        : description}
                    </Marked>
                  </Box>
                </Box>
              )}

              {/** Property details */}
              <AggregatesListingPropertyDetails
                propertyMainType={propertyMainType ?? ''}
                listing={listing}
              />

              {/** Equipment And Characteristics */}
              <AggregatesListingCharacteristics listing={listing} />

              {/** Need to add online since on mobile */}

              {/** Agency details */}
              {agencyName != null && (
                <Box pr={[0, 2]} mb={4}>
                  <Box as="h2" py={3} css={media([text.h6, text.h5])}>
                    {t('agencyDetails')}
                  </Box>

                  <Flex css={{ gap: '16px' }} pb={3}>
                    {agencyLogoUrl != null && (
                      <Box
                        flexShrink={0}
                        alignSelf="flex-start"
                        css={{
                          border: `1px solid ${colors.grey300}`,
                          borderRadius: shape.borderRadius,
                          padding: '12px',
                        }}
                      >
                        <Image
                          src={agencyLogoUrl}
                          objectFit="contain"
                          css={media({
                            width: ['6rem', '7rem'],
                            height: ['6rem', '7rem'],
                          })}
                        />
                      </Box>
                    )}

                    <Box width={[1]} css={{ maxWidth: 360 }}>
                      <Box
                        css={[
                          media([text.subtitle1, text.subtitle0]),
                          { lineHeight: 1.25 },
                        ]}
                      >
                        {agencyName ?? '-'}
                      </Box>

                      {agencyRating != null && (
                        <RatingComponent
                          rating={agencyRating}
                          reviewsCount={agencyReviewsCount}
                        />
                      )}

                      {agencySlug != null && (
                        <Link href={getAgencyLink(language, agencySlug)}>
                          <a
                            css={[
                              text.body2,
                              text.linkToggle,
                              {
                                fontWeight: 'bold',
                                '&:hover': {
                                  textDecoration: 'underline',
                                },
                              },
                            ]}
                          >
                            {t('viewAgencyProfile')}
                          </a>
                        </Link>
                      )}

                      {agencyContactPhoneNumber != null && (
                        <Box pb={1}>
                          <a
                            onClick={
                              !showAgencyPhoneNumber
                                ? () =>
                                    handleShowPhoneNumber(
                                      setShowAgencyPhoneNumber,
                                    )
                                : undefined
                            }
                            href={
                              showAgencyPhoneNumber
                                ? `tel:${agencyContactPhoneNumber}`
                                : undefined
                            }
                            css={[text.body2, text.linkToggle]}
                          >
                            {showAgencyPhoneNumber
                              ? agencyContactPhoneNumber
                              : t('showNumber')}
                          </a>
                        </Box>
                      )}

                      {visitContactPerson != null && (
                        <p css={media([text.subtitle2, text.subtitle1])}>
                          {visitContactPerson}
                        </p>
                      )}

                      {visitContactPhoneNumber != null && (
                        <Box>
                          <a
                            onClick={
                              !showVisitPhoneNumber
                                ? () =>
                                    handleShowPhoneNumber(
                                      setShowVisitPhoneNumber,
                                    )
                                : undefined
                            }
                            href={
                              showVisitPhoneNumber
                                ? `tel:${visitContactPhoneNumber}`
                                : undefined
                            }
                            css={[text.body2, text.linkToggle]}
                          >
                            {showVisitPhoneNumber
                              ? visitContactPhoneNumber
                              : t('showNumber')}
                          </a>
                        </Box>
                      )}
                    </Box>
                  </Flex>
                </Box>
              )}

              {/** Energy diagnostics */}
              <AggregatesListingEnergyDiagnostics listing={listing} />

              {/** Location */}
              <Box pr={[0, 2]} mb={4}>
                <>
                  <Box as="h2" py={3} css={media([text.h6, text.h5])}>
                    {t('location')}
                  </Box>
                  {postcode != null && (
                    <Box width={[1, 1 / 2]} pb={3}>
                      {route != null && route !== '' && (
                        <p css={text.subtitle1}>
                          {[route, ' ', streetNumber].join('')}
                        </p>
                      )}
                      <p css={text.subtitle1}>
                        {postcode} {subLocality ?? '-'}{' '}
                        {cantonCode != null && <>({cantonCode})</>}
                      </p>
                    </Box>
                  )}
                  {postcode == null && (
                    <Box width={[1, 1 / 2]} pb={3}>
                      <p css={text.subtitle1}>{address}</p>
                    </Box>
                  )}

                  <AggregatesListingMap listing={listing} />
                </>
              </Box>

              <Flex
                mb={5}
                p={3}
                flexWrap="wrap"
                flexDirection="column"
                alignItems="start"
                css={media({
                  position: 'relative',
                  height: '100%',
                  width: '100%',
                  background: `url(/static/images/switzerland-map-corner.png)`,
                  backgroundPosition: 'right bottom',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '40%',
                  backgroundColor: colors.background.card,
                  borderRadius: shape.borderRadius,
                  display: ['flex', 'none', 'none'],
                })}
              >
                <Box
                  css={[
                    text.body1,
                    {
                      color: colors.grey600,
                    },
                  ]}
                >
                  {t('notWhatYouWereLookingForQuestion')}
                </Box>

                <Box
                  mt={3}
                  mb={3}
                  css={[
                    text.subtitle1,
                    {
                      maxWidth: '65%',
                    },
                  ]}
                >
                  {t('browseSimilarListingsInPlace', {
                    placeName: similarDataLabel,
                    count: similarDataCount,
                  })}
                </Box>

                <Box mt={3}>
                  <Link href={props.backHref}>
                    <Button
                      color="primary"
                      size="small"
                      component="a"
                      target="_blank"
                      rel="noopener"
                    >
                      {t('seeListings')}
                      <ArrowRightLong
                        size={18}
                        css={{
                          position: 'relative',
                          top: '1px',
                          left: '3px',
                        }}
                      />
                    </Button>
                  </Link>
                </Box>
              </Flex>

              <AggregatesListingBreadcrumbs listing={listing} />

              {/** Sources */}
              {/** Only show sources for realadvisor listings for admins */}
              {url?.originalDomain.includes('realadvisor.ch') &&
              (me == null || me.isAdmin === false) ? (
                <Box pb={3}></Box>
              ) : (
                (offlineSince == null || me?.isAdmin === true) && (
                  <Box pr={[0, 2]} py={3} mb={2}>
                    <Box py={3} css={media([text.h6, text.h5])}>
                      {t('source', { count: urls.length })}
                    </Box>
                    <Box css={text.body1}>
                      <Flex flexWrap="wrap">
                        {urls.map((item, inx) => (
                          <Box
                            key={inx}
                            mr={2}
                            css={{ minWidth: '150px' }}
                            mb={[3, 3, 2]}
                          >
                            <Flex>
                              <Box
                                mr={1}
                                target="_blank"
                                onClick={() => onLinkoutClick(item)}
                                css={[text.body2, text.link]}
                              >
                                {domainToSitename(item.originalDomain)}
                              </Box>
                              <Box
                                css={[text.body2, text.link]}
                                onClick={() => onLinkoutClick(item)}
                              >
                                <OpenInNew fill={colors.teal900} size={18} />
                              </Box>
                            </Flex>
                            {me?.isAdmin === true &&
                              item.originalDomain.includes(
                                'realadvisor.ch',
                              ) && (
                                <Box mr={1} css={[text.body2, text.ellipsis]}>
                                  {listing.isIdxListing
                                    ? '(IDX)'
                                    : '(Agency name)'}
                                </Box>
                              )}
                            {me?.isAdmin === true && (
                              <Flex>
                                <Box mr={1} css={[text.body2, text.ellipsis]}>
                                  {agencyReferences[inx] != null &&
                                    `${t('agencyReference')} `}
                                  {agencyReferences[inx]}
                                </Box>
                              </Flex>
                            )}
                          </Box>
                        ))}
                      </Flex>
                    </Box>
                  </Box>
                )
              )}
            </Box>

            {/** Price card */}
            {/*
            TODO: DONT RENDER ON MOBILE AT ALL
            USE SAME IDEA AS lazy.js control

           */}
            <Box
              mt={'-80px'}
              pl={[3, 3, 4]}
              css={media({
                minWidth: ['280px', '300px', '360px'],
                display: ['none', 'block'],
                position: ['unset', 'sticky', 'sticky'],
                top: '20px',
              })}
            >
              <AggregatesListingPriceCard
                listing={listing}
                onLinkoutClick={onLinkoutClick}
                isListingCampaign={isListingCampaign}
              />
            </Box>
          </Flex>

          {/** Similar Listings */}
          <Box pb={5}>
            <Box py={3} css={media([text.h6, text.h5])}>
              {t('similarListings')}
            </Box>
            <Box
              mt={3}
              css={{
                display: 'grid',
                gridTemplateColumns: `repeat(auto-fill, minmax(${240}px, 1fr))`,
                gridGap: '16px',
              }}
            >
              {similarListings.map(similar => (
                <AggregatesListingCard
                  key={similar.id}
                  listing={similar}
                  offerType={similar.offerType}
                  onLinkClick={onLinkClick(similar)}
                  shallowLink={props.shallowCardsLink}
                  replaceLink={props.replaceCardsLink}
                />
              ))}
            </Box>
          </Box>
        </Center>

        {/** Button view source */}
        {url != null && (
          <Box
            width={1}
            css={media({
              display: ['unset', 'none'],
            })}
          >
            <AppBar color="inherit" css={{ bottom: 0, top: 'auto' }}>
              <Divider />
              <Center>
                <Box py={2}>
                  {isListingCampaign === false ? (
                    <Button
                      color="primary"
                      onClick={() => onLinkoutClick(urls[0])}
                    >
                      {bookOnline === true
                        ? t('bookOnline')
                        : t('contactAgent')}
                    </Button>
                  ) : (
                    <Button
                      onClick={() => setSendEnquiryDialog(true)}
                      color="primary"
                    >
                      {t('sendEnquiry')}
                    </Button>
                  )}
                </Box>
              </Center>
            </AppBar>
          </Box>
        )}
      </>
    );
  });
